import { cn } from '@reflex/ui/lib/utils';
import { SearchSelect, type SearchSelectOption } from '@reflex/ui/components/shared/SearchSelect';
import { FILTER_ALL_VALUE, type FilterOption } from './filter-types';

interface FilterSearchSelectProps {
  label: string;
  value: string;
  onValueChange: (value: string) => void;
  options: FilterOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  className?: string;
}

/**
 * Standalone searchable filter select: label above, combobox below.
 *
 * For a single facet that lives on its own in a page toolbar. Lists narrowed
 * by several facets use {@link FilterBar}, which spells the applied ones out
 * as chips instead of parking a permanent control per facet. Thin wrapper
 * around the shared {@link SearchSelect} — supplies the toolbar `sm` sizing
 * and the "all/none applied" muted-trigger convention.
 */
export function FilterSearchSelect({
  label,
  value,
  onValueChange,
  options,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  className,
}: FilterSearchSelectProps) {
  const isActive = value !== FILTER_ALL_VALUE;
  const selectedLabel = options.find((o) => o.value === value)?.label ?? placeholder;
  const items: SearchSelectOption<string>[] = options.map((opt) => ({
    key: opt.value,
    value: opt.value,
    label: opt.label,
    searchValue: opt.label,
  }));

  return (
    <div className={cn('space-y-1', className)}>
      <span className="block text-2xs font-medium text-muted-foreground">{label}</span>
      <SearchSelect
        ariaLabel={label}
        size="sm"
        triggerLabel={selectedLabel}
        className={cn(!isActive && 'text-muted-foreground')}
        options={items}
        value={value}
        onChange={onValueChange}
        selectedFirst={false}
        searchPlaceholder={searchPlaceholder}
        emptyMessage="No results"
      />
    </div>
  );
}
